import React, { useEffect, useRef } from 'react';
import { AnimatePresence } from 'framer-motion';
import './styles/index.css';
import ChatWindow from './components/ChatWindow';
import ChatButton from './components/ChatButton';
import { useChatWidget } from './hooks/useChatWidget';
import { Helmet } from 'react-helmet-async';
import { Message } from './utils/chatUtils';
import { FormField } from './utils/chatUtils';

interface ChatWidgetProps {
  initialMessage?: string;
  showSettings?: boolean;
}

// Define types for the wrapper components
interface ChatWindowWrapperProps {
  isOpen: boolean;
  isMobile: boolean;
  botConfig: { botName: string; botSubtitle: string };
  messages: Message[];
  currentMessage: string;
  isTyping: boolean;
  isContactFormMode: boolean;
  contactFormStep: FormField | 'confirmation' | null;
  isSubmitting: boolean;
  messagesEndRef: React.RefObject<HTMLDivElement>;
  showSettings: boolean;
  toggleChat: () => void;
  startContactForm: () => void;
  exitContactForm: () => void;
  setCurrentMessage: (message: string) => void;
  handleSendMessage: () => void;
  handleKeyPress: (e: React.KeyboardEvent) => void;
}

// Separate component for the chat window to avoid conditional rendering of hooks
const ChatWindowWrapper: React.FC<ChatWindowWrapperProps> = ({ isOpen, ...props }) => {
  if (!isOpen) return null;
  return <ChatWindow isOpen={isOpen} {...props} />;
};

interface ChatButtonWrapperProps {
  isOpen: boolean;
  onClick: () => void;
}

// Separate component for the chat button to avoid conditional rendering of hooks
const ChatButtonWrapper: React.FC<ChatButtonWrapperProps> = ({ isOpen, onClick }) => {
  if (isOpen) return null;
  return <ChatButton onClick={onClick} />;
};

const ChatWidget = ({ 
  initialMessage = 'Olá! Sou o *Assistente Moa* da Perícia Técnica. Como posso ajudá-lo hoje?',
  showSettings = true // Changed default to true to ensure the mail icon is visible
}: ChatWidgetProps) => {
  const {
    isOpen,
    messages,
    currentMessage,
    isTyping,
    isContactFormMode,
    contactFormStep,
    isSubmitting,
    messagesEndRef,
    botConfig,
    isMobile,
    toggleChat,
    startContactForm,
    exitContactForm,
    setCurrentMessage,
    handleSendMessage,
    handleKeyPress
  } = useChatWidget({ initialMessage });
  
  // Create a ref for the chat widget container
  const chatWidgetRef = useRef<HTMLDivElement>(null);
  
  // Add click outside listener to close the chat
  useEffect(() => {
    // Only add the listener if the chat is open
    if (!isOpen) return;
    
    const handleClickOutside = (event: MouseEvent) => {
      // Check if the click was outside the chat widget
      if (chatWidgetRef.current && !chatWidgetRef.current.contains(event.target as Node)) {
        // Don't close if clicking on the chat button or its wrapper
        const chatButtonWrapper = document.querySelector('.chat-button-wrapper');
        const chatButton = document.querySelector('.chat-button');
        
        if ((chatButtonWrapper && chatButtonWrapper.contains(event.target as Node)) || 
            (chatButton && chatButton.contains(event.target as Node))) {
          return;
        }
        
        // Close the chat
        toggleChat();
      }
    };
    
    // Add the event listener
    document.addEventListener('mousedown', handleClickOutside);
    
    // Clean up the event listener
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, toggleChat]);
  
  // Add structured data for the chat widget
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "potentialAction": {
      "@type": "CommunicateAction",
      "instrument": {
        "@type": "SoftwareApplication",
        "name": "Assistente Moa Chat",
        "applicationCategory": "ChatBot",
        "operatingSystem": "Web"
      },
      "target": {
        "@type": "EntryPoint",
        "urlTemplate": window.location.href,
        "inLanguage": "pt-BR"
      }
    }
  };
  
  // Add meta description for the chat widget
  useEffect(() => {
    // Add chat widget description to the page's meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      const currentDescription = metaDescription.getAttribute('content') || '';
      if (!currentDescription.includes('assistente virtual')) {
        metaDescription.setAttribute(
          'content',
          `${currentDescription} Converse com nosso assistente virtual para obter ajuda imediata.`
        );
      }
    }
    
    // Add chat widget keywords to the page's meta keywords
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      const currentKeywords = metaKeywords.getAttribute('content') || '';
      if (!currentKeywords.includes('chat')) {
        metaKeywords.setAttribute(
          'content',
          `${currentKeywords}, chat, assistente virtual, atendimento online, suporte`
        );
      }
    }
  }, []);
  
  return (
    <>
      {/* Add structured data for SEO */}
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>
      
      {/* Overlay for desktop only - completely skip on mobile */}
      {!isMobile && (
        <div 
          className={`fullscreen-overlay ${isOpen ? 'active' : ''}`} 
          aria-hidden={!isOpen}
          onClick={toggleChat}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            backdropFilter: 'blur(4px)',
            WebkitBackdropFilter: 'blur(4px)',
            zIndex: 9998,
            display: isOpen ? 'block' : 'none',
            opacity: isOpen ? 1 : 0,
            pointerEvents: isOpen ? 'auto' : 'none',
          }}
        />
      )}
      
      {/* Chat widget container with ref */}
      <div ref={chatWidgetRef} className="chat-widget-container">
        {/* Chat window */}
        <ChatWindowWrapper
          isOpen={isOpen}
          isMobile={isMobile}
          botConfig={botConfig}
          messages={messages}
          currentMessage={currentMessage}
          isTyping={isTyping}
          isContactFormMode={isContactFormMode}
          contactFormStep={contactFormStep}
          isSubmitting={isSubmitting}
          messagesEndRef={messagesEndRef}
          showSettings={showSettings}
          toggleChat={toggleChat}
          startContactForm={startContactForm}
          exitContactForm={exitContactForm}
          setCurrentMessage={setCurrentMessage}
          handleSendMessage={handleSendMessage}
          handleKeyPress={handleKeyPress}
        />
        
        {/* Chat button */}
        <div className="chat-button-wrapper">
          <ChatButtonWrapper isOpen={isOpen} onClick={toggleChat} />
        </div>
      </div>
    </>
  );
};

export default ChatWidget;
